/**
 * @author      Yamini Mishra
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import {
  Col,
  Form,
  Modal,
  Row,
  Button,
  ProgressBar,
  Badge,
} from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";
import * as constants from "../../constants/CONSTANT";

const QuickRepliesTemplateModel = ({
  show,
  onHide,
  contactData, // Can be single contact or array of contacts for bulk
  refreshData,
  filterData,
  selectedWhatsAppSetting,
  isBulk = false, // New prop to indicate bulk mode
  onBulkSendComplete, // Callback for when bulk sending completes
}) => {
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );

  const [isSpinner, setIsSpinner] = useState(false);
  const [body, setBody] = useState([]);
  const [selectedMessage, setselectedMessage] = useState(null);
  const docxIcon = "/user_images/doc-icon.png";
  const imgIcon = "/user_images/placeholder.jpg";

  // Bulk sending states
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResults, setBulkResults] = useState({
    success: 0,
    failed: 0,
    total: 0,
  });

  useEffect(() => {
    fetchData(selectedWhatsAppSetting);
  }, [selectedWhatsAppSetting]);

  const fetchData = async (selectedWhatsAppSetting) => {
    try {
      const result = await WhatsAppAPI.fetchInteractiveMessage(
        selectedWhatsAppSetting,
      );
      if (result.success) {
        setBody(result.records);
      } else {
        setBody([]);
      }
    } catch (error) {
      setBody([]);
      console.error("Error fetching data:", error);
    } finally {
      setIsSpinner(true);
    }
  };

  const handleChange = async (event) => {
    const selectedName = event.target.value;
    if (selectedName) {
      const template = body.find((t) => t.id === selectedName);
      setselectedMessage(template);
    } else {
      setselectedMessage();
    }
  };

  // Single message sending function
  const sendSingleMessage = async (contact) => {
    let fullNumber;

    if (filterData?.recordType === "recentlyMessage") {
      fullNumber = contact.whatsapp_number?.startsWith("+")
        ? contact.whatsapp_number
        : `+${contact.whatsapp_number}`;
    } else {
      const rawNumber = contact.whatsapp_number?.toString().replace(/^\+/, "");
      const countryCode = (contact.country_code || "91").replace(/^\+?/, "+");
      fullNumber = `${countryCode}${rawNumber}`;
    }

    try {
      let documentId = null;
      const fileUrl = `${constants.FRONTEND_URL}/public/${userInfo.tenantcode}/attachment/${encodeURIComponent(selectedMessage.file_title)}`;

      if (selectedMessage?.header_type !== "text") {
        const fileData = {
          header: selectedMessage.header_type?.toUpperCase(),
          header_body: fileUrl,
        };
        const documentResult = await WhatsAppAPI.fetchDocumentId(
          fileData,
          selectedWhatsAppSetting,
        );
        documentId = documentResult.documentId;
      }

      let headerPayload = null;
      const mediaType = selectedMessage.header_type?.toLowerCase();

      if (mediaType === "text" && selectedMessage.header_body) {
        headerPayload = {
          type: "text",
          text: selectedMessage.header_body,
        };
      } else if (
        ["image", "video", "document"].includes(mediaType) &&
        documentId
      ) {
        headerPayload = {
          type: mediaType,
          [mediaType]: {
            id: documentId,
            ...(mediaType === "document" && selectedMessage?.filename
              ? { filename: selectedMessage.filename }
              : {}),
          },
        };
      }

      const reqBody = {
        messaging_product: "whatsapp",
        recipient_type: "individual",
        to: fullNumber,
        type: "interactive",
        interactive: {
          type: selectedMessage.type === "list" ? "list" : "button",
          ...(headerPayload && { header: headerPayload }),
          body: {
            text: selectedMessage.body_text,
          },
          ...(selectedMessage.footer && {
            footer: { text: selectedMessage.footer },
          }),
          action:
            selectedMessage.type === "list"
              ? {
                  button: selectedMessage.sections?.[0]?.title
                    ? selectedMessage.sections[0].title
                    : "Choose",
                  sections: (selectedMessage.sections || []).map((section) => ({
                    title: section.title,
                    rows: (section.rows || []).map((row) => ({
                      id: row.id,
                      title: row.title,
                      description: row.description || "",
                    })),
                  })),
                }
              : {
                  buttons: (selectedMessage.buttons || []).map((btn) => ({
                    type: "reply",
                    reply: { id: btn.id, title: btn.text },
                  })),
                },
        },
      };

      const result = await WhatsAppAPI.sendSingleWhatsAppTextMessage(
        reqBody,
        selectedWhatsAppSetting,
      );

      if (result.error) {
        throw new Error(
          result.error.error_data?.details ||
            result.error.message ||
            "Failed to send",
        );
      } else {
        const messageId = result.messages[0]?.id;
        await WhatsAppAPI.insertMsgHistoryRecords({
          parent_id: contact.id || null,
          name: contact?.contactname || "",
          message_template_id: null,
          whatsapp_number: fullNumber,
          message: "",
          status: "Outgoing",
          recordtypename: filterData?.recordType || "",
          file_id: selectedMessage.file_id || null,
          is_read: true,
          business_number: selectedWhatsAppSetting,
          message_id: messageId,
          interactive_id: selectedMessage.id,
        });

        return {
          success: true,
          contact: contact.contactname || contact.whatsapp_number,
        };
      }
    } catch (error) {
      console.error(`Error sending to ${contact.whatsapp_number}:`, error);
      return {
        success: false,
        contact: contact.contactname || contact.whatsapp_number,
        error: error.message,
      };
    }
  };

  // Bulk sending function
  const sendBulkMessages = async () => {
    if (!selectedMessage || !isBulk) return;

    setBulkSending(true);
    setBulkResults({ success: 0, failed: 0, total: contactData.length });
    setBulkProgress(0);

    const results = [];

    for (let i = 0; i < contactData.length; i++) {
      const contact = contactData[i];

      try {
        const result = await sendSingleMessage(contact);
        results.push(result);

        if (result.success) {
          setBulkResults((prev) => ({ ...prev, success: prev.success + 1 }));
        } else {
          setBulkResults((prev) => ({ ...prev, failed: prev.failed + 1 }));
        }
      } catch (error) {
        results.push({
          success: false,
          contact: contact.contactname || contact.whatsapp_number,
          error: error.message,
        });
        setBulkResults((prev) => ({ ...prev, failed: prev.failed + 1 }));
      }

      // Update progress
      const progress = Math.round(((i + 1) / contactData.length) * 100);
      setBulkProgress(progress);

      // Small delay to prevent rate limiting
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    setBulkSending(false);

    // Show summary
    if (bulkResults.success > 0) {
      toast.success(`Successfully sent to ${bulkResults.success} contacts`);
    }
    if (bulkResults.failed > 0) {
      toast.warning(`Failed to send to ${bulkResults.failed} contacts`);
    }

    // Callback if provided
    if (onBulkSendComplete) {
      onBulkSendComplete(results);
    }

    // Close modal after a short delay
    setTimeout(() => {
      onHide();
      if (refreshData) refreshData();
    }, 2000);
  };

  // Single message handler
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isBulk) {
      await sendBulkMessages();
      return;
    }

    setIsSpinner(false);
    try {
      const result = await sendSingleMessage(contactData);
      if (result.success) {
        toast.success("Message sent successfully");
      } else {
        toast.error(`Failed to send message: ${result.error}`);
      }
    } catch (error) {
      toast.error("Failed to send message.");
    } finally {
      setIsSpinner(true);
      refreshData();
      onHide();
    }
  };

  const renderHeader = () => {
    const fileUrl = selectedMessage?.file_id
      ? `/public/${userInfo.tenantcode}/attachment/${selectedMessage.file_title}`
      : imgIcon;

    switch (selectedMessage?.header_type) {
      case "image":
        return (
          <img
            src={fileUrl}
            alt="Template Header"
            className="img-fluid rounded w-100 mb-2"
            style={{ height: "200px", objectFit: "cover" }}
          />
        );
      case "video":
        return (
          <video
            src={fileUrl}
            controls
            className="img-fluid rounded w-100 mb-2"
            style={{ height: "200px", objectFit: "cover" }}
          />
        );
      case "document":
        return (
          <a href={fileUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={docxIcon}
              alt="Document Thumbnail"
              className="img-fluid rounded w-100 mb-2"
              style={{ height: "200px", objectFit: "cover" }}
            />
          </a>
        );
      default:
        return (
          <h5 className="fw-bold text-dark">
            {selectedMessage?.header_content}
          </h5>
        );
    }
  };

  const renderFormattedMessage = () => {
    if (!selectedMessage?.body_text) return null;

    const formattedText = selectedMessage?.body_text.replace(
      /(\n)+/g,
      "<br />",
    );
    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  const renderFooter = () =>
    selectedMessage?.footer_text && (
      <div className="template-footer text-muted mt-2">
        {selectedMessage.footer_text}
      </div>
    );

  const renderButtons = () =>
    selectedMessage?.buttons?.length > 0 && (
      <div className="template-buttons mt-3">
        {selectedMessage.buttons.map((btn, index) => (
          <Button key={index} variant="outline-secondary" className="me-2 mb-2">
            {btn.text}
          </Button>
        ))}
      </div>
    );

  const renderSections = () =>
    selectedMessage?.sections?.length > 0 && (
      <div className="template-sections mt-3">
        {selectedMessage.sections.map((section, idx) => (
          <div key={idx}>
            <h6 className="fw-bold mb-3">{section.title}</h6>
            <hr className="text-muted" />
            <div className="d-flex flex-wrap">
              {section.rows.map((row) => (
                <div key={row.id} className="form-check ms-4">
                  <input
                    type="radio"
                    className="form-check-input"
                    name={`section-${idx}`}
                    id={`row-${row.id}`}
                  />
                  <label className="form-check-label" htmlFor={`row-${row.id}`}>
                    {row.title}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );

  return (
    <>
      <Modal show={show} animation={false} size="lg" centered>
        <Modal.Header closeButton onClick={onHide}>
          <Modal.Title id="contained-modal-title-vcenter">
            {isBulk
              ? `Send Quick Reply to ${contactData.length} Contacts`
              : "Send Quick Replies"}
          </Modal.Title>
        </Modal.Header>
        {isSpinner ? (
          <>
            <Modal.Body style={{ maxHeight: "70vh", overflow: "auto" }}>
              {/* Bulk sending progress */}
              {isBulk && bulkSending && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Sending messages...</span>
                    <span>{bulkProgress}%</span>
                  </div>
                  <ProgressBar
                    now={bulkProgress}
                    label={`${bulkProgress}%`}
                    animated
                  />
                  <div className="mt-2">
                    <Badge bg="success" className="me-2">
                      ✓ {bulkResults.success} Sent
                    </Badge>
                    <Badge bg="danger">✗ {bulkResults.failed} Failed</Badge>
                  </div>
                </div>
              )}

              <Form noValidate>
                <Row className="p-2 mb-3">
                  <Col lg={12} sm={12} xs={12}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="form-view-label"
                        htmlFor="categoryFilter"
                      >
                        Select Quick Reply
                      </Form.Label>
                      <Form.Select
                        style={{ height: "36px" }}
                        aria-label="Select Template Category"
                        name="categoryFilter"
                        onChange={handleChange}
                        disabled={bulkSending}
                      >
                        <option value="">All Quick Reply</option>
                        {body?.map((category) => (
                          <option key={category?.id} value={category?.id}>
                            {category?.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={12} sm={12} xs={12}>
                    <>
                      {selectedMessage && (
                        <>
                          <div className="whatsapp-background w-100">
                            <div className="template-box">
                              {renderHeader()}
                              <div className="template-content">
                                {renderFormattedMessage()}
                                {renderFooter()}
                                {renderButtons()}
                                {renderSections()}
                              </div>
                            </div>
                          </div>

                          {/* Bulk info */}
                          {isBulk && !bulkSending && (
                            <div className="alert alert-info mt-3">
                              <i className="fas fa-info-circle me-2"></i>
                              This quick reply will be sent to{" "}
                              <strong>{contactData.length} contacts</strong>.
                              The operation will continue in the background
                              after you click "Send".
                            </div>
                          )}
                        </>
                      )}
                    </>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>

            <Modal.Footer>
              <button
                className="btn btn-light"
                onClick={onHide}
                disabled={bulkSending}
              >
                Close
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={handleSubmit}
                disabled={!selectedMessage || bulkSending}
              >
                {bulkSending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Sending...
                  </>
                ) : (
                  `Send ${isBulk ? `to ${contactData.length} Contacts` : "Message"}`
                )}
              </button>
            </Modal.Footer>
          </>
        ) : (
          <div className="sk-cube-grid">
            <div className="sk-cube sk-cube1"></div>
            <div className="sk-cube sk-cube2"></div>
            <div className="sk-cube sk-cube3"></div>
            <div className="sk-cube sk-cube4"></div>
            <div className="sk-cube sk-cube5"></div>
            <div className="sk-cube sk-cube6"></div>
            <div className="sk-cube sk-cube7"></div>
            <div className="sk-cube sk-cube8"></div>
            <div className="sk-cube sk-cube9"></div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default QuickRepliesTemplateModel;
